import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastWarning } from '@components/Toast';

import GoToMeasurementModal from '@features/measurement/components/GoToMeasurementModal';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	fetchAllSizeCategory,
	fetchSizeCategoryById,
	removeSizeFromProduct
} from '@services/measurementService';

import ApiClient from '@lib/apiClient';

const SelectSizeCategory = ({
	productId,
	selectedItem,
	setIsAddSize,
	onAddSize,
	onRemoveSize,
	colorIds,
	onAddNewSize,
	isFromCreateOrder
}) => {
	const [selectedSizeCategoryId, setSelectedSizeCategoryId] = useState(null);
	const [selectedSizeCategory, setSelectedSizeCategory] = useState('');
	const [sizeCategoryList, setSizeCategoryList] = useState([]);
	const [standardSizeList, setStandardSizeList] = useState([]);
	const [selectedSizeList, setSelectedSizeList] = useState([]);
	const [defaultList, setDefaultList] = useState([]);
	const [openModal, setOpenModal] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const renderDefaultSelectedList = () => {
		let sizeList =
			selectedItem?.colorWiseSizeQuantityPairList?.length > 0
				? selectedItem?.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList
				: selectedItem?.sizeQuantityPairList;
		if (sizeList && sizeList.length > 0) {
			let code = [];
			let selectedSizes = [];
			sizeList.forEach((item) => {
				code.push(item.id);
				selectedSizes.push(item);
			});
			setDefaultList(code);
			setSelectedSizeList(selectedSizes || []);
		}
	};

	useEffect(() => {
		renderDefaultSelectedList();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				dispatch(openLoader());
				const sizeCategory = await fetchAllSizeCategory(`?productId=${productId}`);
				setSizeCategoryList(sizeCategory?.map((item) => ({ id: item.id, value: item.name })));
				const data = await fetchSizeCategoryById(selectedItem?.sizeCategoryId);
				setStandardSizeList(data.productSizeList);
				setSelectedSizeCategory(data.name);
				setSelectedSizeCategoryId(selectedItem?.sizeCategoryId);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		})();
	}, []);

	const onItemClick = async (item) => {
		if (item?.title === 'Size Category') {
			try {
				dispatch(openLoader());
				setSelectedSizeCategoryId(item.id);
				setSelectedSizeCategory(item.value);
				const data = await fetchSizeCategoryById(item.id);
				setStandardSizeList(data.productSizeList);
				setSelectedSizeCategory(data.name);
				setSelectedSizeCategoryId(item.id);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const isSelectedColor = (item) => {
		return selectedSizeList.find((size) => size.id === item.id);
	};

	const onSizeClick = async (item) => {
		if (isSelectedColor(item)) {
			const sizes = selectedSizeList?.filter((size) => size.id !== item.id);
			if (sizes.length === 0) {
				toastWarning('At least one size is required!');
			} else {
				try {
					dispatch(openLoader());
					const data = await removeSizeFromProduct({
						productId,
						productSizeId: item.id,
						measurementType: 'PP_SAMPLE',
						isInstantRemoved: false
					});
					if (data === true) {
						setSelectedSizeList([...sizes]);
					} else {
						setOpenModal(true);
					}
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
					toastError('Failed to remove size');
				}
			}
		} else {
			setSelectedSizeList((prev) => [...prev, item]);
		}
	};

	const handleUpdate = async () => {
		let sizeList = [];
		selectedSizeList.forEach((size) => {
			sizeList.push(size.id);
		});
		let tmpSelectedList = [...selectedSizeList];
		let unselectedList = [];
		defaultList.forEach((id) => {
			let index = tmpSelectedList.findIndex((item) => item.id === id);
			if (index !== -1) {
				tmpSelectedList.splice(index, 1);
			} else {
				unselectedList.push(id);
			}
		});

		let queryParams = sizeList.join(',');
		if (location.pathname === '/orders/create' || isFromCreateOrder) {
			queryParams += '&productId=' + selectedItem.productId + '&removeIfNotExist=false';
		} else {
			queryParams += '&rfqId=' + selectedItem.id + '&removeIfNotExist=false';
		}
		if (colorIds) queryParams += `&colorIds=${colorIds}`;
		queryParams += `&sizeCategoryId=${selectedSizeCategoryId}`;
		ApiClient.POST('addAndRemoveSize', '', '?sizeIdList=' + queryParams)
			.then(async (response) => {
				if (response?.data?.payload) {
					unselectedList.forEach((item) => {
						try {
							onRemoveSize(item);
						} catch (error) {
							toastError(error);
						}
					});
					await onAddSize({}, response.data.payload);
				} else {
					toastError('Failed to update size');
				}
				setIsAddSize(false);
			})
			.catch((error) => {
				toastError(error?.response?.data?.message);
			});
	};

	const handleSearch = () => {};

	const goToMeasurementPage = () => {
		navigate(`/designs/edit/${productId}?activeTab=measurement`);
		setOpenModal(false);
	};

	return (
		<>
			<div className="add-sizep-category-modal shadow-24dp">
				<div className="design-info-container d-flex">
					<div className="right-half">
						<div className="design-update-table">
							<table className="design-basic-info-table">
								<tbody>
									<tr className="sizes-row">
										<td colSpan={4}>
											<div className="fabric-select">
												<CustomDropdown
													isAddNew={true}
													type="addItem"
													title={'Size Category'}
													onItemClick={(value) => {
														if (value.isAddNew) {
															onAddNewSize();
														} else {
															onItemClick(value);
														}
													}}
													items={sizeCategoryList}
													selectedItem={selectedSizeCategory}
													onItemSearch={handleSearch}
													hideSearchBox={true}
												/>
											</div>
										</td>
										{standardSizeList?.slice(0, 6).map((item) => (
											<td
												className={`size ${isSelectedColor(item) ? 'selected' : ''}`}
												key={item.id}
												onClick={() => onSizeClick(item)}
											>
												<span>{item.label}</span>
											</td>
										))}
									</tr>
									<tr className="sizes-row">
										{standardSizeList?.slice(6, standardSizeList.length).map((item) => (
											<td
												className={`size ${isSelectedColor(item) ? 'selected' : ''}`}
												key={item.id}
												onClick={() => onSizeClick(item)}
											>
												<span>{item.label}</span>
											</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>

				<div className="footer-buttons text-right mt-2">
					<span
						className="regular-14 gray_dark_02 cursor-pointer"
						onClick={() => setIsAddSize(false)}
					>
						CANCEL
					</span>
					<span className="regular-14 gray_dark_02 ml-3 cursor-pointer" onClick={handleUpdate}>
						UPDATE
					</span>
				</div>
			</div>
			{openModal && (
				<GoToMeasurementModal
					onClose={() => setOpenModal(false)}
					title={
						'You cannot change this size category here because it has a measurement. To unselect it, go to Design edit > Measurement chart.'
					}
					onSubmit={goToMeasurementPage}
				/>
			)}
		</>
	);
};

export default SelectSizeCategory;
